import { DecimalPipe } from '@angular/common';
import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';

import { AnalyticsDonutComponent } from '../../../shared/components/analytics/analytics-donut/analytics-donut.component';
import { AnalyticsSeriesChartComponent } from '../../../shared/components/analytics/analytics-series-chart/analytics-series-chart.component';
import {
  PrettySelectComponent,
  PrettySelectOption,
} from '../../../shared/components/ui/pretty-select/pretty-select.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import {
  AnalyticsService,
  isUnresolvedScope,
} from '../../../shared/services/analytics.service';
import { LocaleService } from '../../../shared/services/locale.service';
import {
  PeriodPreset,
  RatingSummary,
  SeriesPoint,
  SettlementSummary,
  resolvePeriod,
} from '../../../shared/types/analytics.types';
import { TPipe } from '../../../shared/pipe/t.pipe';

/**
 * Section analytics du dashboard **owner** (slice 0 : règlements + notes).
 *
 * Auto-scopée : le back dérive le périmètre du JWT, le front n'envoie aucun
 * identifiant. Le 403 « acteur non résolvable » (backfill non passé) a son
 * **propre message**, distinct d'une erreur générique.
 *
 * ⚠️ Ne jamais additionner deux scopes : `grossAmount`, `platformAmount` et
 * `operatorAmount` sont ici les **colonnes d'un même périmètre** (le loueur),
 * pas des scopes distincts — leur combinaison (`net = gross - platform -
 * operator`) est donc licite.
 */
@Component({
  selector: 'app-owner-analytics',
  standalone: true,
  imports: [
    DecimalPipe,
    FormsModule,
    TPipe,
    PrettySelectComponent,
    AnalyticsSeriesChartComponent,
    AnalyticsDonutComponent,
  ],
  templateUrl: './owner-analytics.component.html',
})
export class OwnerAnalyticsComponent implements OnInit {
  private readonly analytics = inject(AnalyticsService);
  private readonly locale = inject(LocaleService);
  private readonly destroyRef = inject(DestroyRef);

  readonly period = signal<PeriodPreset>('30d');
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  /** `true` = acteur non résolvable (403) → message dédié, pas une erreur. */
  readonly unresolved = signal(false);

  readonly settlement = signal<SettlementSummary | null>(null);
  readonly rating = signal<RatingSummary | null>(null);
  readonly series = signal<SeriesPoint[]>([]);

  readonly periodOptions: PrettySelectOption<PeriodPreset>[] = [
    { value: '7d', label: 'analytics.period.7d' },
    { value: '30d', label: 'analytics.period.30d' },
    { value: '90d', label: 'analytics.period.90d' },
    { value: '365d', label: 'analytics.period.365d' },
  ];

  /** Net loueur estimé : GMV moins les deux commissions prélevées. */
  readonly netAmount = computed(() => {
    const s = this.settlement();
    if (!s) return 0;
    return s.grossAmount - s.platformAmount - s.operatorAmount;
  });

  ngOnInit(): void {
    this.load();
  }

  changePeriod(p: PeriodPreset | null): void {
    if (!p || p === this.period()) return;
    this.period.set(p);
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.unresolved.set(false);
    const period = resolvePeriod(this.period());

    forkJoin({
      settlement: this.analytics.settlementSummary(period),
      rating: this.analytics.ratingSummary(period),
      series: this.analytics.settlementSeries(period),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ settlement, rating, series }) => {
          this.settlement.set(settlement);
          this.rating.set(rating);
          this.series.set(series);
          this.loading.set(false);
        },
        error: (err: unknown) => {
          this.loading.set(false);
          if (isUnresolvedScope(err)) {
            this.unresolved.set(true);
          } else {
            this.error.set(toErrorMessage(err, 'errors.analyticsLoadFailed'));
          }
        },
      });
  }

  /** Libellé de la note moyenne — jamais `0` quand il n'y a pas d'avis. */
  ratingLabel(): string {
    const r = this.rating();
    if (!r || r.average == null) return this.locale.t('analytics.noRatings');
    return r.average.toFixed(1);
  }

  seriesName(): string {
    return this.locale.t('analytics.chart.revenue');
  }
}
